import './EventParticipantViewDatatable.scss';

import { Cancel } from '@mui/icons-material';
import { Button, Chip, Hidden, IconButton, Pagination, Stack } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import classNames from 'classnames';
import { EventStatusEnum } from 'domain/event/EventStatusEnum';
import {
  EventParticipant,
  EventParticipantFilters,
} from 'domain/event/participants/EventParticipant';
import { EventParticipantState } from 'domain/event/participants/EventParticipantState';
import { EventParticipantStatusEnum } from 'domain/event/participants/EventParticipantStatusEnum';
import { UnpyEvent } from 'domain/event/UnpyEvent';
import { UnpyEventView } from 'domain/event/UnpyEventView';
import { setModalConfirmOpen } from 'domain/modal/store/ModalSlice';
import { TeamStatusEnum } from 'domain/team/TeamStatusEnum';
import { insertIf } from 'helper/array.helper';
import { EventParticipantCardListItem } from 'primary/Components/Event/EventParticipantCardListItem';
import { KeyValue } from 'primary/Components/KeyValue/KeyValue';
import { LoadingScreen } from 'primary/Components/LoadingScreen/LoadingScreen';
import { NoContentBloc } from 'primary/Components/NoContentBloc/NoContentBloc';
import { getTeamStatusObject } from 'primary/events/datatable/TeamsViewDatatableParameters';
import { UseFetchWithFilterPageReturn } from 'primary/hooks/useFetchWithFilterPage';
import React from 'react';
import { useDispatch } from 'react-redux';

import { PlayerTeam } from '../../../domain/event/PlayerTeam';
import { Team } from '../../../domain/team/Team';
import { Translate } from '../../../domain/translation/Translation.repository';
import { TagPlayer } from '../../Components/TagPlayer/TagPlayer';
import { useContextDependency } from '../../hooks/useContextDependency';
import { useTranslate } from '../../hooks/useTranslate';

interface EventParticipantDatatableParametersProps {
  asView?: boolean;
  reFetch: () => void;
  event: UnpyEvent | UnpyEventView;
  disabled?: boolean;
  readOnly?: boolean;
  useFetchReturn: UseFetchWithFilterPageReturn<EventParticipant, EventParticipantFilters>;
  participantsState?: EventParticipantState;
}

export const getParticipantStatusObject = (
  eventParticipant: EventParticipant,
  t: Translate,
) => {
  return {
    label: t(`enum.eventParticipantStatus.${eventParticipant.status}`),
    state: [
      EventParticipantStatusEnum.WAIT_VALID,
      EventParticipantStatusEnum.WAIT_VALIDATE,
      EventParticipantStatusEnum.PAYMENT_REQUIRED,
    ].includes(eventParticipant.status)
      ? 'warning'
      : EventParticipantStatusEnum.REGISTERED === eventParticipant.status
      ? 'success'
      : 'error',
  };
};

const formatTeamsToRowData = (eventParticipants: EventParticipant[], t: Translate) => {
  return eventParticipants.map((eventParticipant) => ({
    id: eventParticipant.id,
    name: eventParticipant.team.name,
    players: eventParticipant.team.players,
    statut: eventParticipant,
    action: eventParticipant,
  }));
};

export const EventParticipantDatatable = (
  props: EventParticipantDatatableParametersProps,
) => {
  if (props.useFetchReturn.loading) return <LoadingScreen loading />;
  if (props.asView) {
    return <EventParticipantCardList {...props} asView />;
  }
  return (
    <>
      <Hidden smDown>
        <EventParticipantDatatableDesktop {...props} />
      </Hidden>
      <Hidden smUp>
        <EventParticipantCardList {...props} />
      </Hidden>
    </>
  );
};

export const useEventParticipantsList = (reFetch: () => void, eventId: number) => {
  const { eventParticipantRepository } = useContextDependency();

  const onClickDelete = (teamId: number) =>
    eventParticipantRepository
      .deleteEventParticipantForEvent(teamId, eventId)
      .then(() => reFetch());

  const onClickAccept = (teamId: number) => () =>
    eventParticipantRepository
      .responseParticipationEvent(true, teamId, eventId)
      .then(() => reFetch());

  const onClickDecline = (teamId: number) => () =>
    eventParticipantRepository
      .responseParticipationEvent(false, teamId, eventId)
      .then(() => reFetch());

  return { onClickDelete, onClickAccept, onClickDecline };
};

export const EventParticipantCardList = ({
  reFetch,
  event,
  disabled,
  useFetchReturn,
  readOnly,
  asView,
  participantsState,
}: EventParticipantDatatableParametersProps) => {
  const { onClickDelete, onClickAccept, onClickDecline } = useEventParticipantsList(
    reFetch,
    event.id,
  );
  const dispatch = useDispatch();
  const openModalConfirmKickTeam = (teamId: number, teamName: string) => {
    dispatch(
      setModalConfirmOpen({
        props: {
          title: t('event.parameters.participants.deleteNoticeTitle', {
            name: teamName,
          }),
          action: () => onClickDelete(teamId),
          description: t('event.parameters.participants.deleteNotice'),
        },
      }),
    );
  };
  const { fetchedResource, goToPage } = useFetchReturn;
  const t = useTranslate();
  return (
    <div className={classNames('eventParticipantCardList', { '-asView': asView })}>
      {participantsState && (
        <div className={'-participantsState'}>
          <KeyValue
            wrap
            alignStart
            accent
            keyValue={'Total inscrits'}
            value={`${participantsState?.totalRegistered} / ${participantsState?.maxParticipants}`}
          />
          <KeyValue
            wrap
            alignStart
            accent
            keyValue={'En attentes'}
            value={`${participantsState?.totalWaitingApproove}`}
          />
        </div>
      )}
      <div className={'-pagination'}>
        {fetchedResource?.content?.length !== 0 && (
          <Pagination
            key={fetchedResource?.currentPage}
            count={fetchedResource?.pagesCount || 1}
            page={fetchedResource?.currentPage ? fetchedResource?.currentPage + 1 : 1}
            size={'medium'}
            onChange={(e, value) => goToPage(value - 1)}
          />
        )}
      </div>
      <div className={'-cardList'}>
        {fetchedResource?.content?.length !== 0 &&
          fetchedResource?.content?.map((participant) => (
            <EventParticipantCardListItem
              key={participant.id}
              asView={asView}
              participant={participant}
              // @ts-ignore
              actions={
                !readOnly && {
                  openModalConfirmKickTeam,
                  onClickAccept,
                  onClickDecline,
                  disabled: !!readOnly,
                }
              }
            />
          ))}
        {!fetchedResource?.content?.length && (
          <NoContentBloc text={t('general.noContentList.eventParticipants')} />
        )}
      </div>
    </div>
  );
};

export const EventParticipantDatatableDesktop = ({
  reFetch,
  event,
  disabled,
  useFetchReturn,
  readOnly,
  participantsState,
}: EventParticipantDatatableParametersProps) => {
  const t = useTranslate();
  const { onClickDelete, onClickAccept, onClickDecline } = useEventParticipantsList(
    reFetch,
    event.id,
  );
  const dispatch = useDispatch();
  const openModalConfirmKickTeam = (teamId: number, teamName: string) => {
    dispatch(
      setModalConfirmOpen({
        props: {
          title: t('event.parameters.participants.deleteNoticeTitle', {
            name: teamName,
          }),
          action: () => onClickDelete(teamId),
          description: t('event.parameters.participants.deleteNotice'),
        },
      }),
    );
  };
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName:
        t(
          `event.teams.datatable.${event?.randomTeams ? 'labelNameRandom' : 'labelName'}`,
        ) +
        ` (${participantsState?.totalRegistered ?? 0} / ${
          participantsState?.maxParticipants
        } inscrits)`,
      flex: 1,
      headerClassName: '-header',
      headerAlign: 'center',
      sortable: false,
    },
    {
      field: 'players',
      headerName: t('event.teams.datatable.labelPlayers'),
      flex: 2,
      sortable: false,
      headerAlign: 'center',
      headerClassName: '-header',
      renderCell: (value: GridRenderCellParams<Team>) => {
        if (!value.value) return;
        return (
          <div className={'-playersLink'}>
            {value.value.map((player: PlayerTeam) => (
              <TagPlayer key={player?.player?.idPlayer} player={player.player} embed />
            ))}
          </div>
        );
      },
    },
    {
      field: 'statut',
      headerName:
        t('event.teams.datatable.labelStatut') +
        ` (${participantsState?.totalWaitingApproove} demandes en attentes)`,
      flex: 2,
      sortable: false,
      headerClassName: '-header',
      headerAlign: 'center',
      cellClassName: '-statut',
      renderCell: (
        value: GridRenderCellParams<{
          label: string;
          state: 'success' | 'error' | 'warning';
        }>,
      ) => (
        <>
          <Chip
            label={getParticipantStatusObject(value.value, t)?.label}
            variant="outlined"
            //@ts-ignore
            color={
              value.value ? getParticipantStatusObject(value.value, t)?.state : 'warning'
            }
          />
          {value.value?.team?.status === TeamStatusEnum.WAIT_VALIDATE_PLAYERS && (
            <Chip
              style={{ marginLeft: '4px' }}
              label={getTeamStatusObject(value.value?.team, t)?.label}
              variant="outlined"
              //@ts-ignore
              color={getTeamStatusObject(value.value?.team, t)?.state}
            />
          )}
        </>
      ),
    },
    //@ts-ignore
    ...insertIf(!readOnly, {
      field: 'action',
      headerName: t('event.teams.datatable.labelActions'),
      headerClassName: '-header',
      headerAlign: 'center',
      sortable: false,
      flex: 1,
      renderCell: (value: GridRenderCellParams<Team>) => {
        if (!value.value) return;
        if (event.status === EventStatusEnum.ON_GOING)
          return <span>Aucune action possible</span>;
        return (
          <div className={'-actions'}>
            {value.value.status === EventParticipantStatusEnum.WAIT_VALID && (
              <>
                <Button
                  variant={'contained'}
                  color={'error'}
                  onClick={onClickDecline(value.value.id)}
                  disabled={disabled}
                >
                  {t('event.teams.datatable.declineAction')}
                </Button>
              </>
            )}
            {value.value.status === EventParticipantStatusEnum.WAIT_VALIDATE && (
              <>
                <Button
                  variant={'contained'}
                  color={'success'}
                  onClick={onClickAccept(value.value.id)}
                  disabled={disabled}
                >
                  {t('event.teams.datatable.acceptAction')}
                </Button>
                <Button
                  variant={'contained'}
                  color={'error'}
                  onClick={onClickDecline(value.value.id)}
                  disabled={disabled}
                >
                  {t('event.teams.datatable.declineAction')}
                </Button>
              </>
            )}
            {value.value.status === EventParticipantStatusEnum.REGISTERED && (
              <IconButton
                color={'error'}
                onClick={() =>
                  openModalConfirmKickTeam(value.value.id, value.value.team.name)
                }
                disabled={disabled}
              >
                <Cancel />
              </IconButton>
            )}
            {value.value.status === EventParticipantStatusEnum.REJECTED && (
              <span>{t('event.teams.datatable.noAction')}</span>
            )}
          </div>
        );
      },
    }),
  ];

  return (
    <div className={'eventParticipantsParameters'}>
      <DataGrid
        //@ts-ignore
        disableColumnReorder
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableDensitySelector
        disableEval
        disableVirtualization
        disableRowSelectionOnClick
        pagination
        getRowHeight={() => 'auto'}
        paginationMode={'server'}
        rowCount={useFetchReturn.fetchedResource?.totalElementsCount}
        paginationModel={{
          pageSize: useFetchReturn.fetchedResource?.pageSize || 0,
          page: useFetchReturn.fetchedResource?.currentPage || 0,
        }}
        onPaginationModelChange={({ pageSize, page }) => {
          if (pageSize !== useFetchReturn.fetchedResource?.pageSize) {
            useFetchReturn.changeNbPerPage(pageSize);
          }
          useFetchReturn.goToPage(page);
        }}
        loading={useFetchReturn.loading}
        className={'-parameters'}
        autoHeight
        sx={{ color: 'white' }}
        slots={{
          // @ts-ignore

          noRowsOverlay: (props) => (
            <Stack {...props} height="100%" alignItems="center" justifyContent="center">
              Aucun participant
            </Stack>
          ),
        }}
        rows={
          useFetchReturn.fetchedResource?.content
            ? formatTeamsToRowData(useFetchReturn.fetchedResource?.content, t)
            : []
        }
        columns={columns}
      />
    </div>
  );
};
